import React, { useState } from 'react';
import { FormControl, InputLabel, Select, MenuItem, IconButton } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Delete } from '@mui/icons-material';
import dayjs from 'dayjs';

import BorderedButton from '../buttons/BorderedButton';

const textFieldStyles = {
  '& .MuiOutlinedInput-root': {
    color: 'white',
    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
    '&:hover fieldset': { borderColor: '#F6911D' },
    '&.Mui-focused fieldset': { borderColor: '#F6911D' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#F6911D' },
  "& input[type='time']::-webkit-calendar-picker-indicator": {
    filter: 'invert(0.6)',
  },
  "& input[type='time']:before": {
    content: '""',
    display: 'none',
  },
};

const selectStyles = {
  ...textFieldStyles,
  '& .MuiSelect-select': { color: 'white' },
};

const menuProps = {
  PaperProps: {
    sx: {
      backgroundColor: '#1a1a1a',
      color: 'white',
      '& .MuiMenuItem-root': {
        '&:hover': {
          backgroundColor: 'rgba(246, 145, 29, 0.1)',
        },
        '&.Mui-selected': {
          backgroundColor: 'rgba(246, 145, 29, 0.2)',
          '&:hover': {
            backgroundColor: 'rgba(246, 145, 29, 0.3)',
          },
        },
      },
    },
  },
};

const timeFieldStyles = {
  width: '100%',
  '& .MuiPickersOutlinedInput-root': {
    backgroundColor: 'transparent',
    '& fieldset': {
      borderColor: 'rgba(255,255,255,0.2)',
    },
    '&:hover fieldset': {
      borderColor: '#F6911D',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#F6911D',
    },
  },
  '& .MuiPickersSectionList-sectionContent': {
    color: 'white !important',
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255,255,255,0.6)',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#F6911D',
  },
  '& .MuiSvgIcon-root': {
    color: '#F6911D',
  },
  '& .MuiFormHelperText-root': {
    color: '#ef4444',
  },
};

const timePopperStyles = {
  '& .MuiPaper-root': { backgroundColor: '#1a1a1a', color: 'white' },
  '& .MuiPickersClock-root': { backgroundColor: '#1a1a1a' },
  '& .MuiClock-pin': { backgroundColor: '#F6911D' },
  '& .MuiClockPointer-root': { backgroundColor: '#F6911D' },
  '& .MuiPickersClockNumber-root': {
    color: 'white',
    '&:hover': { backgroundColor: 'rgba(246,145,29,0.2)' },
    '&.Mui-selected': {
      backgroundColor: '#F6911D',
      '&:hover': { backgroundColor: '#E54004' },
    },
  },
};

const timeDialogStyles = {
  '& .MuiDialog-paper': { backgroundColor: '#1a1a1a', color: 'white' },
  '& .MuiPickersToolbar-root': { backgroundColor: '#1a1a1a' },
  '& .MuiPickersToolbarText-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiPickersToolbarText-root.Mui-selected': { color: 'white' },
  '& .MuiClock-root': { backgroundColor: '#1a1a1a' },
  '& .MuiClock-pin': { backgroundColor: '#F6911D' },
  '& .MuiClockPointer-root': { backgroundColor: '#F6911D' },
  '& .MuiClockNumber-root': { color: 'white' },
  '& .MuiDialogActions-root': { backgroundColor: '#1a1a1a' },
  '& .MuiButton-root': { color: '#F6911D' },
};

function TimeSlotField({
  timeSlots,
  setTimeSlots,
  // subjects = [],
  // medium = [],
  // cities = [],
  classTypes = [],
  label = 'Time Slots',
  required = false,
}) {
  const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  const typeOptions =
    classTypes.length > 0
      ? classTypes
      : [
          { id: 1, name: 'Individual Home Visits' },
          { id: 2, name: 'Individual Online' },
          { id: 3, name: 'Group Home Visits' },
          { id: 4, name: 'Group Online' },
        ];

  const [currentSlot, setCurrentSlot] = useState({
    day: '',
    name: '',
    startTime: '',
    endTime: '',
    type: '',
    subject: '',
    medium: '',
    city: '',
  });

  const isValid =
    currentSlot.day &&
    currentSlot.startTime &&
    currentSlot.endTime &&
    currentSlot.type

  const handleAdd = () => {
    if (!isValid) return;

    // Format time for display
    const formatTime = (time) => {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    };

    // Add full object for display
    const slotWithFormattedTime = {
      ...currentSlot,
      time: `${formatTime(currentSlot.startTime)} - ${formatTime(currentSlot.endTime)}`,
    };

    setTimeSlots({
      ...timeSlots,
      [currentSlot.day]: [...(timeSlots[currentSlot.day] || []), slotWithFormattedTime],
    });

    // Clear current slot
    setCurrentSlot({
      day: '',
      name: '',
      startTime: '',
      endTime: '',
      type: '',
      subject: '',
      medium: '',
      city: '',
    });
  };

  const handleRemove = (day, index) => {
    const updatedSlots = [...timeSlots[day]];
    updatedSlots.splice(index, 1);
    setTimeSlots({
      ...timeSlots,
      [day]: updatedSlots,
    });
  };

  return (
    <div className="space-y-4">
      <label className="block text-white font-semibold">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div className="space-y-4 p-4 rounded-lg border border-white/10">
        {/* Day & Name */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormControl fullWidth sx={selectStyles}>
            <InputLabel>Day *</InputLabel>
            <Select
              value={currentSlot.day}
              onChange={(e) => setCurrentSlot({ ...currentSlot, day: e.target.value })}
              label="Day *"
              MenuProps={menuProps}
            >
              {days.map((day) => (
                <MenuItem key={day} value={day}>
                  {day}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={selectStyles}>
            <InputLabel>Type *</InputLabel>
            <Select
              value={currentSlot.type}
              onChange={(e) => setCurrentSlot({ ...currentSlot, type: e.target.value })}
              label="Type *"
              MenuProps={menuProps}
            >
              {typeOptions.map((type) => (
                <MenuItem key={type.id} value={type}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/*<InputField*/}
          {/*  label="Class Name *"*/}
          {/*  value={currentSlot.name}*/}
          {/*  onChange={(e) => setCurrentSlot({ ...currentSlot, name: e.target.value })}*/}
          {/*  placeholder="e.g., Grade 10 Science"*/}
          {/*/>*/}
        </div>

        {/* Time & Type */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <TimePicker
              label="Available From"
              value={currentSlot.startTime ? dayjs(currentSlot.startTime, 'HH:mm') : null}
              onChange={(newValue) =>
                setCurrentSlot({
                  ...currentSlot,
                  startTime: newValue ? newValue.format('HH:mm') : null,
                })
              }
              slotProps={{
                textField: { sx: timeFieldStyles },
                popper: { sx: timePopperStyles },
                dialog: { sx: timeDialogStyles },
              }}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <TimePicker
              label="Available To"
              value={currentSlot.endTime ? dayjs(currentSlot.endTime, 'HH:mm') : null}
              onChange={(newValue) =>
                setCurrentSlot({
                  ...currentSlot,
                  endTime: newValue ? newValue.format('HH:mm') : '',
                })
              }
              slotProps={{
                textField: { sx: timeFieldStyles },
                popper: { sx: timePopperStyles },
                dialog: { sx: timeDialogStyles },
              }}
            />
          </LocalizationProvider>
        </div>

        {/* Subject & Medium */}
        {/*<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">*/}
        {/*  <FormControl fullWidth sx={selectStyles}>*/}
        {/*    <InputLabel>Subject *</InputLabel>*/}
        {/*    <Select*/}
        {/*      value={currentSlot.subject}*/}
        {/*      onChange={(e) => setCurrentSlot({ ...currentSlot, subject: e.target.value })}*/}
        {/*      label="Subject *"*/}
        {/*      MenuProps={menuProps}*/}
        {/*    >*/}
        {/*      {subjects.map((subject) => (*/}
        {/*        <MenuItem key={subject.id} value={subject}>*/}
        {/*          {subject.name}*/}
        {/*        </MenuItem>*/}
        {/*      ))}*/}
        {/*    </Select>*/}
        {/*  </FormControl>*/}

        {/*  <FormControl fullWidth sx={selectStyles}>*/}
        {/*    <InputLabel>Medium *</InputLabel>*/}
        {/*    <Select*/}
        {/*      value={currentSlot.medium}*/}
        {/*      onChange={(e) => setCurrentSlot({ ...currentSlot, medium: e.target.value })}*/}
        {/*      label="Medium *"*/}
        {/*      MenuProps={menuProps}*/}
        {/*    >*/}
        {/*      {medium.map((med) => (*/}
        {/*        <MenuItem key={med.id} value={med}>*/}
        {/*          {med.name}*/}
        {/*        </MenuItem>*/}
        {/*      ))}*/}
        {/*    </Select>*/}
        {/*  </FormControl>*/}
        {/*</div>*/}

        {/* City */}
        {/*<FormControl fullWidth sx={selectStyles}>*/}
        {/*  <InputLabel>City (Optional)</InputLabel>*/}
        {/*  <Select*/}
        {/*    value={currentSlot.city || ''}*/}
        {/*    onChange={(e) => setCurrentSlot({ ...currentSlot, city: e.target.value || null })}*/}
        {/*    label="City (Optional)"*/}
        {/*    MenuProps={menuProps}*/}
        {/*  >*/}
        {/*    <MenuItem value="">*/}
        {/*      <em>None</em>*/}
        {/*    </MenuItem>*/}
        {/*    {cities.map((city) => (*/}
        {/*      <MenuItem key={city.id} value={city}>*/}
        {/*        {city.name}*/}
        {/*      </MenuItem>*/}
        {/*    ))}*/}
        {/*  </Select>*/}
        {/*</FormControl>*/}

        <BorderedButton onClick={handleAdd} type="button" fullWidth disabled={!isValid}>
          Add Time Slot
        </BorderedButton>
      </div>

      {/* Time Slots List */}
      {Object.keys(timeSlots).some((day) => timeSlots[day]?.length > 0) && (
        <div className="max-h-64 overflow-y-auto space-y-2">
          {days.map(
            (day) =>
              timeSlots[day]?.length > 0 && (
                <div key={day} className="bg-gradient-glass p-3 rounded-lg border border-white/10">
                  <p className="text-white font-semibold">{day}</p>
                  {timeSlots[day].map((slot, index) => (
                    <div
                      key={index}
                      className="flex items-start justify-between text-sm text-gray-300 mb-2 pb-2 border-b border-white/5 last:border-0"
                    >
                      <div className="flex-1">
                        <div className="font-medium text-white">{slot.name}</div>
                        <div className="text-xs mt-1">
                          <span className="text-orange-400">{slot.time}</span> • {slot.type?.name}
                        </div>
                        {/*<div className="text-xs mt-1">*/}
                        {/*  {slot.subject?.name} • {slot.medium?.name}*/}
                        {/*  {slot.city && ` • ${slot.city?.name}`}*/}
                        {/*</div>*/}
                      </div>
                      <IconButton
                        size="small"
                        onClick={() => handleRemove(day, index)}
                        sx={{ color: '#ef4444', ml: 1 }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </div>
                  ))}
                </div>
              ),
          )}
        </div>
      )}
    </div>
  );
}

export default TimeSlotField;
